import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { FocusOn } from '../../components/FocusOn'
import { isBrowser } from '../../util/browser'
import { BaseButton } from './BaseButton'
import { FoundationIcon } from './FoundationIcon'
import { Layer } from './Layer'
import { getNeutralColorVars } from './ThemeProvider'

const TAKEOVER_Z_INDEX = 300

type Props = {
  children: React.ReactNode
  className?: string
  /**
   * Set this to `true` to disable the focus lock on the `Takeover`. You should only do this
   * on a transient basis when necessary, such as when rendering a `Modal` on top of a
   * `Takeover`, so that the `Modal` can take control of the FocusLock. You should only
   * disable the FocusLock while the `Modal` is open, and you'll need to re-enable it when
   * the `Modal` closes. See https://phabricator.robinhood.com/D315157 for an example of
   * how to do this.
   */
  focusLockDisabled?: boolean
  isCustomContent?: boolean
  onClose: () => void
  /**
   * Callback that will be called when the Takeover finishes animating in.
   */
  onTransitionEnd?: () => void
  overrideCloseButtonStyles?: CSSInterpolation
  overrideDialogStyles?: CSSInterpolation
  shouldShowCloseButton?: boolean
}

export function Takeover({
  children,
  className,
  focusLockDisabled = false,
  isCustomContent = false,
  onClose,
  onTransitionEnd,
  overrideCloseButtonStyles,
  overrideDialogStyles,
  shouldShowCloseButton = false,
}: Props) {
  const rootRef = useRef<null | HTMLDialogElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  const animateIn = useCallback(() => {
    if (!isBrowser()) {
      return
    }

    if (!rootRef.current) {
      window.requestAnimationFrame(() => animateIn())
    } else {
      // NOTE(alex): This hack forces a `reflow` to flush out the
      // current layout to the layout engine.
      // from https://phabricator.robinhood.com/D48112#change-ftsciwaxGAvj
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      rootRef.current.scrollTop
      setIsVisible(true)
    }
  }, [])

  useEffect(() => {
    if (!isBrowser()) {
      return () => {}
    }

    animateIn()
  }, [animateIn])

  // NOTE(joshua): We use noIsolation here to disable the use of the
  //   aria-hidden package (not attribute) which uses JS to make any element
  //   outside of FocusOn inert (non-navigable) because doing so also interferes
  //   with a mouse user's ability to interact with popovers and other content
  //   rendered in its own portal. However, the use of aria-modal here (which is
  //   intended to supercede the use of aria-hidden for dialogs) ensures that
  //   screen readers will not be able to access content outside of the modal.
  return (
    <Layer onEscape={onClose}>
      <FocusOn autoFocus enabled={!focusLockDisabled} noIsolation returnFocus>
        <dialog
          ref={rootRef}
          aria-modal="true"
          className={className}
          css={[
            {
              background: getNeutralColorVars().bg1,
              border: 'unset',
              bottom: 0,
              color: getNeutralColorVars().fg1,
              height: 'unset',
              left: 0,
              opacity: 0,
              overflow: 'auto',
              padding: 0,
              position: 'fixed',
              right: 0,
              top: 0,
              transform: 'translate(0, 100%)',
              transformBox: 'border-box',
              transition: 'opacity 500ms, transform 500ms',
              width: 'unset',
              zIndex: TAKEOVER_Z_INDEX,
            },
            isVisible && {
              opacity: 1,
              transform: 'none',
            },
            !isCustomContent && {
              paddingTop: 120,
            },
            overrideDialogStyles,
          ]}
          onTransitionEnd={onTransitionEnd}
          // The component is designed such that the dialog element should be
          // removed from the DOM when it is not visible. Thus, always setting
          // this to true should not introduce accessibility issues and simplifies
          // animations.
          open
        >
          {isCustomContent ? (
            children
          ) : (
            <div css={{ margin: '0 auto', width: 565 }}>{children}</div>
          )}
          {(!isCustomContent || shouldShowCloseButton) && (
            <CloseTakeoverButton
              onClick={onClose}
              overrideRootStyles={overrideCloseButtonStyles}
            />
          )}
        </dialog>
      </FocusOn>
    </Layer>
  )
}

export function CloseTakeoverButton({
  onClick,
  overrideRootStyles,
}: {
  onClick: () => void
  overrideRootStyles?: CSSInterpolation
}) {
  return (
    <BaseButton
      css={[
        {
          '&:hover': {
            backgroundColor: getNeutralColorVars().bg2,
          },
          alignItems: 'center',
          borderRadius: 26,
          cursor: 'pointer',
          display: 'flex',
          height: 52,
          justifyContent: 'center',
          position: 'fixed',
          right: 24,
          top: 24,
          width: 52,
          zIndex: 1,
        },
        overrideRootStyles,
      ]}
      onClick={onClick}
      type="button"
    >
      <FoundationIcon type="Close24" />
    </BaseButton>
  )
}
